import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { attrToRealId } from './blockClipboard';
import { useTestBlockSelection } from './TestBlockSelectionContext';
import type { RefObject } from 'react';

const DRAG_THRESHOLD_PX = 4;
const EDGE_THRESHOLD_PX = 112;
const MAX_SCROLL_STEP_PX = 48;
/** Auto-scroll keeps accelerating past the container edge, up to this factor. */
const OUTSIDE_BOOST_MAX = 1.5;

/**
 * A marquee only starts from genuinely empty space — page margins, the gray
 * background around pages, gaps between blocks. Presses on anything that
 * carries text or its own interaction (blocks, editable sections, rendered
 * rich text, form/table elements) keep native text selection and
 * click-to-edit.
 */
const MARQUEE_START_EXCLUSION = [
    '[data-preview-block-id]',
    '[data-block-selection-ui]',
    '[data-editable-section-anchor]',
    '[data-report-content]',
    '[data-table-editor]',
    'input',
    'textarea',
    '[contenteditable="true"]',
    'button',
    'a',
    'label',
    'p',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'li',
    'td',
    'th',
    'blockquote',
    'span',
].join(', ');

interface ViewportRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface DragState {
    /** Anchor in container-content coordinates (immune to auto-scroll). */
    startX: number;
    startY: number;
    /** Latest pointer position in viewport coordinates. */
    clientX: number;
    clientY: number;
    /** Selection snapshot at press time; Shift unions the marquee with it. */
    baseSelection: string[];
    union: boolean;
    active: boolean;
}

/**
 * Drag-to-select over the report preview: press on empty page space and drag
 * a rectangle — every test block it touches joins the selection live, with
 * edge auto-scroll for areas taller than the viewport. Shift+drag unions with
 * the existing selection.
 */
export function MarqueeSelectionLayer({
    containerRef,
}: {
    containerRef: RefObject<HTMLDivElement | null>;
}) {
    const { enabled, selectedIds, replaceSelection } = useTestBlockSelection();
    const [overlayRect, setOverlayRect] = useState<ViewportRect | null>(null);

    const dragRef = useRef<DragState | null>(null);
    const rafRef = useRef<number | null>(null);
    const selectedIdsRef = useRef(selectedIds);
    selectedIdsRef.current = selectedIds;
    const replaceSelectionRef = useRef(replaceSelection);
    replaceSelectionRef.current = replaceSelection;

    useEffect(() => {
        const container = containerRef.current;
        if (!enabled || !container) return;

        const toContentPoint = (clientX: number, clientY: number) => {
            const rect = container.getBoundingClientRect();
            return {
                x: clientX - rect.left + container.scrollLeft,
                y: clientY - rect.top + container.scrollTop,
            };
        };

        const stopDrag = () => {
            if (rafRef.current !== null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            document.removeEventListener('pointermove', handlePointerMove);
            document.removeEventListener('pointerup', handlePointerUp);
            document.body.style.userSelect = '';
            dragRef.current = null;
            setOverlayRect(null);
        };

        const tick = () => {
            const drag = dragRef.current;
            if (!drag || !drag.active) {
                rafRef.current = null;
                return;
            }

            const containerRect = container.getBoundingClientRect();

            // Edge auto-scroll: linear ramp across the edge band, full speed
            // at the container edge, boosted (capped) when the pointer is
            // outside the container.
            const scrollStepFor = (edgeDistance: number) => {
                if (edgeDistance >= EDGE_THRESHOLD_PX) return 0;
                const strength = Math.min(
                    (EDGE_THRESHOLD_PX - edgeDistance) / EDGE_THRESHOLD_PX,
                    OUTSIDE_BOOST_MAX,
                );
                return MAX_SCROLL_STEP_PX * strength;
            };
            const topDistance = drag.clientY - containerRect.top;
            const bottomDistance = containerRect.bottom - drag.clientY;
            const scrollStep =
                topDistance < bottomDistance
                    ? -scrollStepFor(topDistance)
                    : scrollStepFor(bottomDistance);
            if (scrollStep !== 0) {
                // 'instant' bypasses the container's scroll-smooth, which would
                // otherwise re-animate every frame and crawl at ~60px/s
                container.scrollBy({ top: scrollStep, behavior: 'instant' });
            }

            // Marquee rect in content coordinates
            const current = toContentPoint(drag.clientX, drag.clientY);
            const left = Math.min(drag.startX, current.x);
            const top = Math.min(drag.startY, current.y);
            const right = Math.max(drag.startX, current.x);
            const bottom = Math.max(drag.startY, current.y);

            // Live intersection with block wrappers (measured fresh each
            // frame in the same coordinate space, so zoom/scroll stay
            // consistent)
            const hits = new Set(drag.union ? drag.baseSelection : []);
            container.querySelectorAll('[data-preview-block-id]').forEach((el) => {
                const attr = el.getAttribute('data-preview-block-id');
                if (!attr) return;

                const blockRect = el.getBoundingClientRect();
                const blockLeft = blockRect.left - containerRect.left + container.scrollLeft;
                const blockTop = blockRect.top - containerRect.top + container.scrollTop;
                const blockRight = blockLeft + blockRect.width;
                const blockBottom = blockTop + blockRect.height;

                const intersects =
                    blockLeft < right &&
                    blockRight > left &&
                    blockTop < bottom &&
                    blockBottom > top;
                if (intersects) hits.add(attrToRealId(attr));
            });
            replaceSelectionRef.current([...hits]);

            // Overlay in viewport coordinates, clipped to the container
            const viewLeft = Math.max(
                left - container.scrollLeft + containerRect.left,
                containerRect.left,
            );
            const viewTop = Math.max(
                top - container.scrollTop + containerRect.top,
                containerRect.top,
            );
            const viewRight = Math.min(
                right - container.scrollLeft + containerRect.left,
                containerRect.right,
            );
            const viewBottom = Math.min(
                bottom - container.scrollTop + containerRect.top,
                containerRect.bottom,
            );
            setOverlayRect({
                left: viewLeft,
                top: viewTop,
                width: Math.max(viewRight - viewLeft, 0),
                height: Math.max(viewBottom - viewTop, 0),
            });

            rafRef.current = requestAnimationFrame(tick);
        };

        const handlePointerMove = (event: PointerEvent) => {
            const drag = dragRef.current;
            if (!drag) return;

            drag.clientX = event.clientX;
            drag.clientY = event.clientY;

            if (!drag.active) {
                const start = toContentPoint(event.clientX, event.clientY);
                const distance = Math.hypot(start.x - drag.startX, start.y - drag.startY);
                if (distance < DRAG_THRESHOLD_PX) return;

                drag.active = true;
                document.body.style.userSelect = 'none';
                rafRef.current = requestAnimationFrame(tick);
            }
        };

        const handlePointerUp = () => {
            const wasActive = dragRef.current?.active;
            stopDrag();

            if (wasActive) {
                // The click that follows a marquee would clear the fresh
                // selection (document pointerdown/click behaviors) — swallow it.
                const swallowClick = (clickEvent: MouseEvent) => {
                    clickEvent.preventDefault();
                    clickEvent.stopPropagation();
                };
                document.addEventListener('click', swallowClick, { capture: true, once: true });
                setTimeout(
                    () => document.removeEventListener('click', swallowClick, { capture: true }),
                    150,
                );
            }
        };

        const handlePointerDown = (event: PointerEvent) => {
            if (event.pointerType !== 'mouse' || event.button !== 0) return;
            if (dragRef.current) return;

            const target = event.target as HTMLElement | null;
            if (!target || target.closest(MARQUEE_START_EXCLUSION)) return;

            const start = toContentPoint(event.clientX, event.clientY);
            dragRef.current = {
                startX: start.x,
                startY: start.y,
                clientX: event.clientX,
                clientY: event.clientY,
                baseSelection: [...selectedIdsRef.current],
                union: event.shiftKey,
                active: false,
            };
            document.addEventListener('pointermove', handlePointerMove);
            document.addEventListener('pointerup', handlePointerUp);
        };

        container.addEventListener('pointerdown', handlePointerDown);
        return () => {
            container.removeEventListener('pointerdown', handlePointerDown);
            stopDrag();
        };
    }, [enabled, containerRef]);

    if (!overlayRect) return null;

    return createPortal(
        <div
            data-block-selection-ui
            className="pointer-events-none fixed z-40 rounded-sm border border-blue-400/70 bg-blue-400/10"
            style={{
                left: overlayRect.left,
                top: overlayRect.top,
                width: overlayRect.width,
                height: overlayRect.height,
            }}
        />,
        document.body,
    );
}
